import {
    Children,
    FC,
    ForwardRefRenderFunction,
    FunctionComponentElement,
    ReactNode,
    forwardRef,
    isValidElement,
    useCallback,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import classnames from 'classnames';

import { throttle } from '@hh.ru/magritte-common-func-utils';
import { useResize } from '@hh.ru/magritte-common-resize';
import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { Segment } from '@hh.ru/magritte-ui-segmented/Segment';
import { SegmentProps, SegmentedProps, SegmentedSize } from '@hh.ru/magritte-ui-segmented/types';
import { Text, TextTypography } from '@hh.ru/magritte-ui-typography';

import styles from './segmented.less';

const LABEL_TYPOGRAPHY = {
    large: {
        selected: 'subtitle-1-semibold',
        unselected: 'label-2-regular',
    },
    medium: {
        selected: 'subtitle-1-semibold',
        unselected: 'label-2-regular',
    },
    small: {
        selected: 'subtitle-2-semibold',
        unselected: 'label-3-regular',
    },
} as Record<SegmentedSize, { [state in 'selected' | 'unselected']: TextTypography }>;

const hasEllipsis = (textElement: HTMLElement) => {
    if (!textElement.parentElement) {
        return false;
    }
    const textWithoutOverflow = textElement.cloneNode(true) as HTMLElement;
    textWithoutOverflow.style.position = 'absolute';
    textWithoutOverflow.style.visibility = 'hidden';
    textElement.parentElement.appendChild(textWithoutOverflow);
    try {
        return textWithoutOverflow.clientWidth > textElement.clientWidth;
    } finally {
        textWithoutOverflow.remove();
    }
};

/**
 * Для того, чтобы ширина `<Segmented />` не прыгала из-за изменения текста на полужирный в выбранном `<Segment />`,
 * его ширина определяется невидимой оберткой с текстом в обычном начертании.
 * Полужирный текст рисуем абсолютом, чтобы он не влиял на ширину.
 * Если в обычном тексте есть эллипсис, полужирный текст ограничиваем такой же шириной.
 * Если в обычном тексте нет эллипсиса, полужирный текст не ограничиваем по ширине, чтобы в нем тоже не было эллипсиса.
 */
const SelectedSegmentText: FC<{ children: ReactNode; size: SegmentedSize }> = ({ children, size }) => {
    const [fixedWidth, setFixedWidth] = useState<boolean | null>(null);
    const textElementRef = useRef<HTMLDivElement>(null);
    const prevTextWidth = useRef<number | null>(null);

    // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    const recalcLayout = useCallback(
        throttle(() => {
            if (textElementRef.current !== null && textElementRef.current.clientWidth !== prevTextWidth.current) {
                setFixedWidth(hasEllipsis(textElementRef.current));
                prevTextWidth.current = textElementRef.current.clientWidth;
            }
        }, 50),
        []
    );

    useLayoutEffect(recalcLayout);
    useResize(recalcLayout);

    return (
        <div className={styles.textWrapper}>
            <div className={classnames({ [styles.invisibleText]: fixedWidth !== null })}>
                <Text typography={LABEL_TYPOGRAPHY[size].unselected}>
                    <div className={styles.text} ref={textElementRef}>
                        {children}
                    </div>
                </Text>
            </div>
            {fixedWidth !== null && (
                <div
                    className={classnames({ [styles.fixedWidthText]: fixedWidth, [styles.freeWidthText]: !fixedWidth })}
                >
                    <Text typography={LABEL_TYPOGRAPHY[size].selected}>
                        <div className={styles.text}>{children}</div>
                    </Text>
                </div>
            )}
        </div>
    );
};

const SegmentedForwardRefRenderFunc: ForwardRefRenderFunction<HTMLDivElement, SegmentedProps> = (
    { children, mode, name, onChange, selectedValue, size = 'medium', stretched },
    ref
) => {
    const id = useId();
    const [actualName, setActualName] = useState(name);
    const forceDisabled = useDisabled();

    useLayoutEffect(() => {
        if (!name) {
            setActualName(id);
        }
    }, [id, name, setActualName]);

    if (forceDisabled) {
        return null;
    }

    return (
        <div
            className={classnames(styles.segmentsContainer, {
                [styles[`${size}Container`]]: !!size,
                [styles.stretchedContainer]: mode === 'label' && stretched,
            })}
            ref={ref}
        >
            {mode === 'icon' &&
                Children.map(children, (child) => {
                    if (!isValidElement(child) || child.type !== Segment) {
                        return null;
                    }

                    const { children: _, disabled, icon: Icon, value, ...props } = child.props as SegmentProps;
                    if (!Icon || disabled) {
                        return null;
                    }
                    const selected = selectedValue === value;
                    return (
                        <label
                            className={styles.segmentWrapper}
                            ref={(child as FunctionComponentElement<SegmentedProps>).ref}
                        >
                            <input
                                {...props}
                                checked={selected}
                                className={styles.input}
                                name={actualName}
                                onChange={onChange}
                                type="radio"
                                value={value}
                            />
                            <div className={classnames(styles.icon, styles.segment, { [styles.selected]: selected })}>
                                <Icon />
                            </div>
                        </label>
                    );
                })}
            {mode === 'label' &&
                Children.map(children, (child) => {
                    if (!isValidElement(child) || child.type !== Segment) {
                        return null;
                    }

                    const { children: label, disabled, value, ...props } = child.props as SegmentProps;
                    if (!label || disabled) {
                        return null;
                    }
                    const selected = selectedValue === value;
                    return (
                        <label
                            className={styles.segmentWrapper}
                            ref={(child as FunctionComponentElement<SegmentedProps>).ref}
                        >
                            <input
                                {...props}
                                checked={selected}
                                className={styles.input}
                                name={actualName}
                                onChange={onChange}
                                type="radio"
                                value={value}
                            />
                            <div className={classnames(styles.label, styles.segment, { [styles.selected]: selected })}>
                                {selected ? (
                                    <SelectedSegmentText size={size}>{label}</SelectedSegmentText>
                                ) : (
                                    <Text typography={LABEL_TYPOGRAPHY[size].unselected}>
                                        <div className={styles.text}>{label}</div>
                                    </Text>
                                )}
                            </div>
                        </label>
                    );
                })}
        </div>
    );
};

export const Segmented = forwardRef(SegmentedForwardRefRenderFunc);
